import type { FileMoveMessageDatagramBuf } from "~/classes/generated/message"
import type { FileMoveResponseDatagramBuf } from "~/classes/generated/response"

import { StaticFile } from "~/classes/file/StaticFile"
import { DatagramBuf } from "~/classes/generated/datagram"
import { FileMoveRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseMessageId, parseRequestId, parseSenderId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf, parseEntityPropertySelection } from "~/utils/flatbuffers/entity"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

const console = useLogger("file/move")

export function parseFileMoveRequestDatagramBuf(
  datagram: FileMoveRequestDatagramBuf,
): FileMoveRequestDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    file: parseFile(datagram.file()!),
    offline: datagram.offline(),
    selection: parseEntityPropertySelection(datagram.selection()),
  }
}

export function sendFileMoveRequests(requests: FileMoveRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()
  const builder = new DatagramBufferBuilder(64)
  const requestVector = []
  for (const request of requests) {
    const file = addFileToBuf(builder, request.file)
    let selection
    if (request.selection)
      selection = addEntityPropertySelectionToBuf(builder, request.selection)
    const requestId = addRequestIdToBuf(builder, request.requestId)
    FileMoveRequestDatagramBuf.startFileMoveRequestDatagramBuf(builder)
    FileMoveRequestDatagramBuf.addRequestId(builder, requestId)
    FileMoveRequestDatagramBuf.addFile(builder, file)
    FileMoveRequestDatagramBuf.addOffline(builder, request.offline)
    if (selection)
      FileMoveRequestDatagramBuf.addSelection(builder, selection)
    const moveRequest = FileMoveRequestDatagramBuf.endFileMoveRequestDatagramBuf(builder)
    RequestDatagramBuf.startRequestDatagramBuf(builder)
    RequestDatagramBuf.addFileMoveRequest(builder, moveRequest)
    requestVector.push(RequestDatagramBuf.endRequestDatagramBuf(builder))
  }

  const vector = DatagramBuf.createRequestVector(builder, requestVector)
  DatagramBuf.startDatagramBuf(builder)
  DatagramBuf.addRequest(builder, vector)
  builder.finish(DatagramBuf.endDatagramBuf(builder))
  sendWorkerDatagramBuf(builder.asUint8Array())
}

export function parseFileMoveResponseDatagramBuf(
  datagram: FileMoveResponseDatagramBuf,
): FileMoveResponseDatagram {
  return {
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    file: maybe(parseFile)(datagram.file()),
    offline: datagram.offline(),
    fileId: parseFileId(datagram.fileId()!),
    priorPathHint: parseVector(i => datagram.priorPathHint(i)!, datagram.priorPathHintLength()).map(parseFileId),
  }
}

/** Parse a Flatbuffers representation of a response */
export function handleFileMoveResponseDatagramBuf(datagram: FileMoveResponseDatagramBuf) {
  onFileMoveResponse(parseFileMoveResponseDatagramBuf(datagram))
}

export function onFileMoveResponse(response: FileMoveResponseDatagram, quiet = false) {
  const { emitEntityEvent, emitInterfaceEvent } = useEvents()

  console.debug("File move response", response)

  emitInterfaceEvent("fileMoveResponse", response, response.requestId)

  if (response.error)
    return onFileMoveErrored(response.error, quiet)
  if (!response.file)
    throw new Error("No file in move response")

  const iFile = new StaticFile(response.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), response.offline)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file move response", response)
    throw new Error("no parent id in file move response")
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), response.offline)

  if (response.priorPathHint && response.priorPathHint.length > 0) {
    const oldParent = response.priorPathHint[0]
    emitEntityEvent("childRemoved", oldParent, iFile.inner(), response.offline)
  }
}

export function onFileMoveErrored(error: DatagramError, quiet = false) {
  const { notifyDatagramError } = useAlerts()

  if (!quiet)
    return notifyDatagramError(error, "File move was unsuccessful")
}

export function handleFileMoveMessageDatagramBuf(datagram: FileMoveMessageDatagramBuf) {
  onFileMoveMessage({
    messageId: parseMessageId(datagram.messageId()!),
    senderId: parseSenderId(datagram.senderId()!),
    file: parseFile(datagram.file()!),
    priorPathHint: parseVector(i => datagram.priorPathHint(i)!, datagram.priorPathHintLength()).map(parseFileId),
  })
}

export function onFileMoveMessage(message: FileMoveMessageDatagram) {
  const { emitEntityEvent } = useEvents()

  const iFile = new StaticFile(message.file)
  emitEntityEvent("fileProperties", iFile.id, iFile.inner(), false)

  const parentId = iFile.inferredParentId()
  if (!parentId) {
    console.error("No parent id in file move response", message)
    throw new Error("no parent id in file move response")
  }
  emitEntityEvent("childProperties", parentId, iFile.inner(), false)

  if (message.priorPathHint && message.priorPathHint.length > 0) {
    const oldParent = message.priorPathHint[0]
    emitEntityEvent("childRemoved", oldParent, iFile.inner(), false)
  }
}
